import { STEPS } from '@lumapps/sign-in/routes';

/**
 * Never resend a previous failed attempt's correlationId with a new login request.
 */
const sanitizeQueryString = (queryString: string = '', excludedParams: string[] = ['correlationId']): string =>
    queryString
        .replace(/^\?/, '')
        .split('&')
        .filter((param) => param && !excludedParams.includes(param.split('=')[0]))
        .join('&');

/**
 * `error`/`accountType`/`logout` are sign-in-specific transient UI state (they drive
 * `LoginMessage`'s error/logout banner) and shouldn't be carried over to the
 * forgot-password flow.
 */
const FORGOT_PASSWORD_EXCLUDED_PARAMS = ['correlationId', 'error', 'accountType', 'logout'];

/**
 * Sanitize the URL used to POST the login forms.
 * The React router add a <step> path to the current URL to load the correct
 * component. This extra path is not needed in the API call made through the POST
 * form.
 * Depending on the STEP we are, this method truncate the step part of the URL.
 *
 * @param pathname current pathname (window.location.pathname)
 * @param queryString potential search path in the current URL (window.location.search)
 * @returns the sanitize version of the endpoint ready to be used in a login form
 */
export const sanitizeLoginStepFormUrl = (loginStep: STEPS, pathname: string, queryString: string = '') => {
    const sanitizedPathname = pathname.replace(`/${loginStep}`, '');
    const sanitizedParams = sanitizeQueryString(queryString);

    return sanitizedParams ? `${sanitizedPathname}?${sanitizedParams}` : sanitizedPathname;
};

/**
 * Build the URL of the forgot-password page from the current login page URL.
 * The forgot-password page is always served at the root-relative `/forgot-password`
 * path, regardless of the instance slug the login page was accessed with.
 *
 * @param queryString potential search path in the current URL (window.location.search)
 * @returns the forgot-password URL, keeping the current query params (except sign-in-specific ones)
 */
export const getForgotPasswordUrl = (queryString: string = ''): string => {
    const sanitizedParams = sanitizeQueryString(queryString, FORGOT_PASSWORD_EXCLUDED_PARAMS);

    return sanitizedParams ? `/forgot-password?${sanitizedParams}` : '/forgot-password';
};
